import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cucine, menu } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { UtensilsCrossed, Plus, Edit, Trash2, ChefHat, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";

const STATI_CUCINA = [
  { value: "operativa", label: "Operativa", color: "bg-green-100 text-green-700" },
  { value: "manutenzione", label: "In Manutenzione", color: "bg-yellow-100 text-yellow-700" },
  { value: "non_operativa", label: "Non Operativa", color: "bg-red-100 text-red-700" },
];

const TIPI_CUCINA = [
  { value: "principale", label: "Principale" },
  { value: "ausiliaria", label: "Ausiliaria" },
  { value: "mobile", label: "Mobile" },
];

export default function CucineCampo() {
  const [cucinaDialogOpen, setCucinaDialogOpen] = useState(false);
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [selectedCucina, setSelectedCucina] = useState(null);
  const [cucinaForm, setCucinaForm] = useState({
    nome: "",
    tipo: "principale",
    stato: "operativa",
    capacita_pasti: 100,
    responsabile: "",
    ubicazione: "",
  });
  const [menuForm, setMenuForm] = useState({
    data: new Date().toISOString().split('T')[0],
    colazione: "",
    pranzo: "",
    cena: "",
    allergeni: "",
    note: "",
  });

  const queryClient = useQueryClient();

  const { data: cucineData, isLoading: cucineLoading } = useQuery({
    queryKey: ['cucine'],
    queryFn: cucine.list,
  });

  const { data: menuData, isLoading: menuLoading } = useQuery({
    queryKey: ['menu'],
    queryFn: () => menu.list(),
  });

  const createCucinaMutation = useMutation({
    mutationFn: cucine.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['cucine']);
      setCucinaDialogOpen(false);
      setCucinaForm({ nome: "", tipo: "principale", stato: "operativa", capacita_pasti: 100, responsabile: "", ubicazione: "" });
      toast.success("Cucina creata");
    },
    onError: (error) => toast.error(error.message),
  });

  const updateCucinaMutation = useMutation({
    mutationFn: ({ id, data }) => cucine.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['cucine']);
      setCucinaDialogOpen(false);
      setSelectedCucina(null);
      toast.success("Cucina aggiornata");
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteCucinaMutation = useMutation({
    mutationFn: cucine.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['cucine']);
      toast.success("Cucina eliminata");
    },
    onError: (error) => toast.error(error.message),
  });

  const createMenuMutation = useMutation({
    mutationFn: (data) => menu.create({
      ...data,
      allergeni: data.allergeni ? data.allergeni.split(',').map(a => a.trim()) : [],
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['menu']);
      setMenuDialogOpen(false);
      setMenuForm({ data: new Date().toISOString().split('T')[0], colazione: "", pranzo: "", cena: "", allergeni: "", note: "" });
      toast.success("Menu salvato");
    },
    onError: (error) => toast.error(error.message),
  });

  const handleEditCucina = (cucina) => {
    setSelectedCucina(cucina);
    setCucinaForm({
      nome: cucina.nome,
      tipo: cucina.tipo,
      stato: cucina.stato,
      capacita_pasti: cucina.capacita_pasti,
      responsabile: cucina.responsabile || "",
      ubicazione: cucina.ubicazione || "",
    });
    setCucinaDialogOpen(true);
  };

  const handleSubmitCucina = (e) => {
    e.preventDefault();
    if (selectedCucina) {
      updateCucinaMutation.mutate({ id: selectedCucina.id, data: cucinaForm });
    } else {
      createCucinaMutation.mutate(cucinaForm);
    }
  };

  const getStatoColor = (stato) => {
    return STATI_CUCINA.find(s => s.value === stato)?.color || "bg-slate-100 text-slate-700";
  };

  const capacitaTotale = cucineData?.filter(c => c.stato === "operativa").reduce((acc, c) => acc + (c.capacita_pasti || 0), 0) || 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cucine-campo-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Cucine da Campo</h1>
          <p className="text-slate-500 mt-1">Gestione cucine, refettori e menu</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <UtensilsCrossed className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cucineData?.length || 0}</p>
                <p className="text-xs text-slate-500">Cucine Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <ChefHat className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cucineData?.filter(c => c.stato === "operativa").length || 0}</p>
                <p className="text-xs text-slate-500">Operative</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{capacitaTotale}</p>
                <p className="text-xs text-slate-500">Capacità Pasti</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <CalendarDays className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{menuData?.length || 0}</p>
                <p className="text-xs text-slate-500">Menu Programmati</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="cucine" className="space-y-4">
        <TabsList>
          <TabsTrigger value="cucine">Cucine</TabsTrigger>
          <TabsTrigger value="menu">Menu Giornalieri</TabsTrigger>
        </TabsList>

        {/* Cucine Tab */}
        <TabsContent value="cucine" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={cucinaDialogOpen} onOpenChange={(open) => {
              setCucinaDialogOpen(open);
              if (!open) {
                setSelectedCucina(null);
                setCucinaForm({ nome: "", tipo: "principale", stato: "operativa", capacita_pasti: 100, responsabile: "", ubicazione: "" });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="add-cucina-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Cucina
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{selectedCucina ? "Modifica Cucina" : "Nuova Cucina"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitCucina} className="space-y-4">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      value={cucinaForm.nome}
                      onChange={(e) => setCucinaForm({ ...cucinaForm, nome: e.target.value })}
                      required
                      data-testid="input-cucina-nome"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tipo</Label>
                      <Select value={cucinaForm.tipo} onValueChange={(v) => setCucinaForm({ ...cucinaForm, tipo: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPI_CUCINA.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Stato</Label>
                      <Select value={cucinaForm.stato} onValueChange={(v) => setCucinaForm({ ...cucinaForm, stato: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATI_CUCINA.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="capacita">Capacità Pasti</Label>
                    <Input
                      id="capacita"
                      type="number"
                      value={cucinaForm.capacita_pasti}
                      onChange={(e) => setCucinaForm({ ...cucinaForm, capacita_pasti: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="responsabile">Responsabile</Label>
                    <Input
                      id="responsabile"
                      value={cucinaForm.responsabile}
                      onChange={(e) => setCucinaForm({ ...cucinaForm, responsabile: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ubicazione">Ubicazione</Label>
                    <Input
                      id="ubicazione"
                      value={cucinaForm.ubicazione}
                      onChange={(e) => setCucinaForm({ ...cucinaForm, ubicazione: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setCucinaDialogOpen(false)}>Annulla</Button>
                    <Button type="submit" className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="submit-cucina-btn">
                      {createCucinaMutation.isPending || updateCucinaMutation.isPending ? "Salvataggio..." : "Salva"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cucine-list">
            {cucineData?.map(cucina => (
              <Card key={cucina.id} data-testid={`cucina-card-${cucina.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-orange-50 rounded-lg">
                        <UtensilsCrossed className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold">{cucina.nome}</h3>
                        <Badge variant="outline" className="text-xs mt-1">
                          {TIPI_CUCINA.find(t => t.value === cucina.tipo)?.label}
                        </Badge>
                      </div>
                    </div>
                    <Badge className={getStatoColor(cucina.stato)}>
                      {STATI_CUCINA.find(s => s.value === cucina.stato)?.label}
                    </Badge>
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-slate-600">
                    <p>Capacità: <span className="font-medium">{cucina.capacita_pasti} pasti</span></p>
                    {cucina.responsabile && <p>Responsabile: {cucina.responsabile}</p>}
                    {cucina.ubicazione && <p>Ubicazione: {cucina.ubicazione}</p>}
                  </div>
                  <div className="mt-4 flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEditCucina(cucina)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => {
                      if (window.confirm("Eliminare questa cucina?")) deleteCucinaMutation.mutate(cucina.id);
                    }}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Menu Tab */}
        <TabsContent value="menu" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={menuDialogOpen} onOpenChange={setMenuDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="add-menu-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Menu
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Menu Giornaliero</DialogTitle>
                </DialogHeader>
                <form onSubmit={(e) => { e.preventDefault(); createMenuMutation.mutate(menuForm); }} className="space-y-4">
                  <div>
                    <Label htmlFor="menu-data">Data</Label>
                    <Input
                      id="menu-data"
                      type="date"
                      value={menuForm.data}
                      onChange={(e) => setMenuForm({ ...menuForm, data: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="colazione">Colazione</Label>
                    <Textarea
                      id="colazione"
                      value={menuForm.colazione}
                      onChange={(e) => setMenuForm({ ...menuForm, colazione: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pranzo">Pranzo</Label>
                    <Textarea
                      id="pranzo"
                      value={menuForm.pranzo}
                      onChange={(e) => setMenuForm({ ...menuForm, pranzo: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="cena">Cena</Label>
                    <Textarea
                      id="cena"
                      value={menuForm.cena}
                      onChange={(e) => setMenuForm({ ...menuForm, cena: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="allergeni">Allergeni (separati da virgola)</Label>
                    <Input
                      id="allergeni"
                      value={menuForm.allergeni}
                      onChange={(e) => setMenuForm({ ...menuForm, allergeni: e.target.value })}
                      placeholder="glutine, lattosio..."
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setMenuDialogOpen(false)}>Annulla</Button>
                    <Button type="submit" className="bg-[#0B1E3D] hover:bg-[#1E40AF]" data-testid="submit-menu-btn">
                      Salva Menu
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-4" data-testid="menu-list">
            {menuData?.map(m => (
              <Card key={m.id}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-purple-600" />
                    {new Date(m.data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-3 bg-yellow-50 rounded-lg">
                      <p className="text-xs font-semibold text-yellow-700 mb-1">COLAZIONE</p>
                      <p className="text-sm">{m.colazione || "-"}</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg">
                      <p className="text-xs font-semibold text-orange-700 mb-1">PRANZO</p>
                      <p className="text-sm">{m.pranzo || "-"}</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs font-semibold text-blue-700 mb-1">CENA</p>
                      <p className="text-sm">{m.cena || "-"}</p>
                    </div>
                  </div>
                  {m.allergeni?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {m.allergeni.map((a, i) => (
                        <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                          {a}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {(!menuData || menuData.length === 0) && (
              <div className="text-center py-12 text-slate-500">
                <CalendarDays className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nessun menu programmato</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
